import { BreadNormalCss } from '@/styles/typography';
import styled from 'styled-components';

const Options = styled.div<{ $maxWidth?: boolean }>`
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.lineGrey};
  background: ${({ theme }) => theme.colors.white};
  backdrop-filter: blur(6px);
  width: 100%;
  height: 51px;
  display: flex;
  padding: 4px;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    width: max-content;
    justify-content: space-between;

    ${({ $maxWidth }) =>
      $maxWidth &&
      `
  width: auto !important;
  min-width: 417px;
  flex: 1;
  align-self: stretch;
  `}
  }
`;

const Option = styled.div<{ $active: boolean }>`
  border-radius: 4px;
  background: ${({ $active }) => ($active ? '#EAEAEA' : '#EAEAEA00')};
  padding: 8px 15px;
  cursor: ${({ $active }) => ($active ? 'default' : 'pointer')};
  transition: all 0.2s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  width: 100%;

  .title {
    font-weight: ${({ $active }) => ($active ? 350 : 325)};
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.m}) {
    width: max-content;
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    flex: 1;
    justify-content: center;
    align-items: center;
  }
`;

const Button = styled.div<{ $desktop?: boolean }>`
  display: ${({ $desktop }) => ($desktop ? 'none' : 'flex')};
  width: 100%;
  margin-bottom: 54px;

  * {
    text-decoration: none;
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    display: ${({ $desktop }) => ($desktop ? 'flex' : 'none')};
    margin-bottom: 0;
  }
`;

const ColorOption = styled.button<{ $color: string; $active: boolean }>`
  width: 32px;
  height: 32px;
  background: ${({ $color }) => $color};
  border: none;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: ${({ $active }) => ($active ? 'default' : 'pointer')};

  svg {
    transition: all 0.3s;
  }

  ${({ $active }) =>
    !$active &&
    `
    svg {
      opacity: 0;
    }
  `}
`;

const HiddenTitle = styled.div`
  visibility: hidden;
`;

const Wrapper = styled.div<{ $color?: string; $textColor?: string }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 0;
  position: relative;
  min-height: 200px;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    padding: 44px 0;
    min-height: 591px;
  }

  .swiper {
    max-height: 75vh;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: auto;
    cursor: grab;
    cursor: -webkit-grab;
    cursor: -moz-grab;
  }

  .swiper:active {
    cursor: grabbing;
    cursor: -webkit-grabbing;
    cursor: -moz-grabbing;
  }

  .swiper-slide {
    margin: auto;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .swiper-pagination {
    .swiper-pagination-bullet {
      background: ${({ theme }) => theme.colors.black};
    }
  }

  .custom-swiper-button {
    display: none;
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    .swiper-slide {
      width: calc(100vw / 1.5);
      max-width: 1500px;
    }
  }
`;

const PaginationDot = styled.div<{ $active: boolean }>`
  height: 4px;
  width: 4px;
  border-radius: 10px;
  -webkit-border-image-radius: 10px;
  -moz-border-image-radius: 10px;
  transition: all 0.2s ease-in-out;
  background-color: ${({ theme }) => theme.colors.black};
  opacity: ${({ $active }) => ($active ? 1 : 0.23)};

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.l}) {
    height: 10px;
    width: 10px;
  }
`;

const Image = styled.img`
  width: 100%;
  object-fit: contain;
  user-select: none;
  -moz-user-select: none;
  -khtml-user-select: none;
  -webkit-user-select: none;
  -o-user-select: none;
`;

const NavButton = styled.button<{
  $left?: number;
  $right?: number;
  $rightDesktop?: number;
  $leftDesktop?: number;
}>`
  position: absolute;
  left: ${({ $left }) => $left || 'auto'}px;
  right: ${({ $right }) => $right || 'auto'}px;
  border: none;
  top: 50%;
  transform: translateY(-50%);
  padding: 6px;
  cursor: pointer;
  display: flex !important;
  justify-content: center;
  align-items: center;
  height: 36px;
  width: 36px;

  background-color: ${({ theme }) => theme.colors.white};

  border-radius: 80px;
  -webkit-border-radius: 80px;
  -moz-border-radius: 80px;

  transition: all 0.2s ease-in-out;

  &:hover {
    background-color: ${({ theme }) => theme.colors.orangePeel};
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.l}) {
    height: 50px;
    width: 50px;
    left: ${({ $leftDesktop }) => $leftDesktop}px;
    right: ${({ $rightDesktop }) => $rightDesktop}px;
  }
`;

const Specifications = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  width: 100%;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.s}) {
    grid-template-columns: 1fr 1fr 1fr;
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    gap: 61px;
    display: flex;
    flex-wrap: wrap;
  }
`;

const BreadNormal = styled.span<{ $weight?: number }>`
  ${BreadNormalCss}
  font-weight: ${({ $weight }) => $weight || 325};
  font-size: 14px;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.l}) {
    font-size: 16px;
  }
`;

const S = {
  Options,
  Option,
  Button,
  ColorOption,
  HiddenTitle,
  Wrapper,
  PaginationDot,
  Image,
  NavButton,
  Specifications,
  BreadNormal,
};

export default S;
